import {
  Heading,
  Button,
  InputField,
  AgGrid,
  Spinner,
  ErrorMessage,
  RadioButton,
  MobileResponsiveGrid,
} from "@/common/components";
import {
  useEffect,
  useContext,
  useState,
  useSelector,
  useMemo,
} from "@/common/hooks";
import { PrivateContext } from "@/routes/PrivateRoute";
import OwnerDetails from "./OwnerDetails";
import ProductDetails from "./ProductDetails";
import {
  useSearchProductOwnershipMutation,
  useGetOwnershipInquiryMutation,
} from "@/service/OwnerService";
import AccountDetails from "./AccountDetails";
import MoneySourceDetails from "./MoneySourceDetails";
import TransactionDetails from "./TransactionDetails";
import {
  setOwnerSearchParams,
  setSearchProductOwnershipResult,
  setSelectedOwner,
  setOwnershipInquiryResult,
  resetOwnersState,
} from "@/store/ownersSlice";
import { encryptData, formatOwnershipTypeCode } from "@/utils/Utilities";
import { validate } from "@/utils/Validations";

const OwnerInquiry = () => {
  const {
    setNavHeader,
    labels,
    productSponsorId,
    accessProfileId,
    dispatch,
    setNavCancelFunc,
    setPageHeader,
    debounceErrors,
    navigate,
  } = useContext(PrivateContext);

  const [searchType, setSearchType] = useState("L");
  const [searchKeyword, setSearchKeyword] = useState();
  const [ownerInquiryPlanId, setOwnerInquiryPlanId] = useState("");
  const [rowData, setRowData] = useState();
  const [ownerData, setOwnerData] = useState();
  const [ownerAccountDetails, setOwnerAccountDetails] = useState({});
  const [showMoneySourceDetails, setShowMoneySourceDetails] = useState(false);
  const [selectedTab, setSelectedTab] = useState("accountDetails");
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Data from the Redux store
  const searchParamsFromStore = useSelector(
    (state) => state?.ownersReducer?.ownerSearchParams
  );
  const ownershipSearchFromStore = useSelector(
    (state) => state?.ownersReducer?.searchProductOwnershipResult
  );
  const selectedOwnerFromStore = useSelector(
    (state) => state?.ownersReducer?.selectedOwner
  );
  const ownershipInquiryFromStore = useSelector(
    (state) => state?.ownersReducer?.ownershipInquiryResult
  );

  const columnDefs = [
    {
      headerName: labels?.ownerInquiry?.firstName1,
      field: "firstName",
      sort: "asc",
    },
    { headerName: labels?.ownerInquiry?.lastName1, field: "lastName" },
    {
      headerName: labels?.ownerInquiry?.taxId,
      field: "maskedOwnerTaxId",
    },
    { headerName: labels?.ownerInquiry?.productName, field: "product.name" },
    {
      headerName: labels?.ownerInquiry?.ownershipType,
      field: "ownershipType",
      cellRenderer: formatOwnershipTypeCode,
    },
    {
      headerName: labels?.ownerInquiry?.recordKeeper,
      field: "recordkeeperRecordkeepingSystem.recordkeeperName",
    },
    {
      headerName: labels?.ownerInquiry?.productDetailsPlanId,
      field: "planId",
      flex: 1.5,
    },
  ];

  const getRowClass = (params) => {
    if (
      params.data?.ownerTaxId === ownerData?.ownerTaxId &&
      params.data?.ownershipId === ownerData?.ownershipId
    ) {
      return "hover-bg";
    }
    return "";
  };

  const searchByData = {
    header: labels?.ownerInquiry?.searchBy,
    data: [
      {
        name: "Last Name",
        label: labels?.ownerInquiry?.lastName1,
        id: "L",
      },
      {
        name: "Tax ID",
        label: labels?.ownerInquiry?.taxId,
        id: "T",
      },
      {
        name: "Participant ID",
        label: labels?.ownerInquiry?.participantId,
        id: "P",
      },
    ],
  };

  const clearStateInStore = () => {
    dispatch(resetOwnersState());
    navigate(-1);
    return;
  };

  useEffect(() => {
    setPageHeader(labels?.ownerInquiry?.title);
    setNavHeader(labels?.navbar?.owner);
    setNavCancelFunc(() => clearStateInStore);
  }, []);

  const ownerInquiryPlanIdInputChange = (e) => setOwnerInquiryPlanId(e);

  const [searchProductOwnershipApi, { isLoading: isFetching, error }] =
    useSearchProductOwnershipMutation();
  const [getOwnershipInquiryApi, { isLoading: isFetching2, error2 }] =
    useGetOwnershipInquiryMutation();

  const onRowClickedOwnerInquiry = (e) => {
    setSelectedTab("accountDetails");
    dispatch(setSelectedOwner(e.data));
    const {
      lineOfBusinessId,
      ownershipType,
      ownerTaxId,
      planId,
      product,
      recordkeeperRecordkeepingSystem,
    } = e.data;

    const getOwnershipInquiryPayload = {
      lineOfBusinessId: lineOfBusinessId,
      ownershipTypeCode: ownershipType,
      ownerTaxId: ownerTaxId,
      planId: planId,
      externalProductId: product.externalId,
      recordKeeperName: recordkeeperRecordkeepingSystem.recordkeeperName,
      recordKeepingSystemName:
        recordkeeperRecordkeepingSystem.recordkeepingName,
      productSponsorId: productSponsorId,
      accessProfileId: accessProfileId,
    };

    getOwnershipInquiryApi(getOwnershipInquiryPayload)
      .unwrap()
      .then((res) => {
        if (!res?.errors?.length) dispatch(setOwnershipInquiryResult(res));
      });
  };

  const searchProductOwnerships = async () => {
    dispatch(resetOwnersState());

    const ownerSearchPayload = {
      searchType: searchType,
      searchKeyword:
        searchType === "T" ? await encryptData(searchKeyword) : searchKeyword,
      planId: ownerInquiryPlanId,
      productSponsorId: productSponsorId,
      accessProfileId: accessProfileId,
    };
    dispatch(
      setOwnerSearchParams({
        searchType: searchType,
        searchKeyword: searchKeyword,
        planId: ownerInquiryPlanId,
      })
    );

    searchProductOwnershipApi(ownerSearchPayload)
      .unwrap()
      .then((res) => {
        if (!res.errors.length) {
          dispatch(setSearchProductOwnershipResult(res.productOwnerships));
          setOwnerData();

          if (res.productOwnerships === null)
            dispatch(setSearchProductOwnershipResult([]));

          // If only one result, auto-select it
          if (res?.productOwnerships?.length === 1) {
            const singleOwner = res.productOwnerships[0];
            dispatch(setSelectedOwner(singleOwner));

            const getOwnershipInquiryPayload = {
              lineOfBusinessId: singleOwner.lineOfBusinessId,
              ownershipTypeCode: singleOwner.ownershipType,
              ownerTaxId: singleOwner.ownerTaxId,
              planId: singleOwner.planId,
              externalProductId: singleOwner.product.externalId,
              recordKeeperName:
                singleOwner.recordkeeperRecordkeepingSystem.recordkeeperName,
              recordKeepingSystemName:
                singleOwner.recordkeeperRecordkeepingSystem.recordkeepingName,
              productSponsorId: productSponsorId,
              accessProfileId: accessProfileId,
            };

            getOwnershipInquiryApi(getOwnershipInquiryPayload)
              .unwrap()
              .then((res) => {
                if (!res.errors.length) {
                  dispatch(setOwnershipInquiryResult(res));
                }
              });
          }
        }
      });
  };

  const overlayNoRowsTemplate = useMemo(
    () =>
      `<div class="ag-overlay-no-rows-center">${
        labels?.ownerInquiry?.noData ?? "No data"
      }</div>`,
    [labels]
  );

  const handleTabChange = (tab) => {
    setSelectedTab(tab);
  };

  useEffect(() => {
    if (ownershipSearchFromStore) {
      setRowData(ownershipSearchFromStore);
      setOwnerData(selectedOwnerFromStore);
      setOwnerAccountDetails(ownershipInquiryFromStore);
    }
    if (ownershipInquiryFromStore?.productTypeId === "F") {
      setShowMoneySourceDetails(true);
    } else {
      setShowMoneySourceDetails(false);
    }
  }, [
    ownershipSearchFromStore,
    selectedOwnerFromStore,
    ownershipInquiryFromStore,
  ]);

  useEffect(() => {
    if (searchParamsFromStore) {
      setSearchKeyword(searchParamsFromStore?.searchKeyword);
      setSearchType(searchParamsFromStore?.searchType);
      setOwnerInquiryPlanId(searchParamsFromStore?.planId);
    }
  }, [searchParamsFromStore]);

  useEffect(() => {
    if (searchKeyword && touched.searchKeyword) {
      if (searchType === "T") {
        validate(searchKeyword, "taxId").then((errorMsg) => {
          debounceErrors(
            {
              ...errors,
              searchKeyword: errorMsg,
            },
            setErrors
          );
        });
      } else {
        debounceErrors(
          {
            ...errors,
            searchKeyword: "",
          },
          setErrors
        );
      }
    }
  }, [searchType]);

  return (
    <div>
      <section className="container-x-large py-5">
        {(isFetching || isFetching2) && <Spinner />}
        <ErrorMessage error={error || error2} wrapperClassName="mb-3" />

        <RadioButton
          wrapperClassName="mb-3"
          data={searchByData}
          value={searchType}
          selected={(e) => {
            setSearchType(e);
            if (e === "T") setSearchKeyword((prev) => prev?.replace(/\D/g, ""));
            if (e !== "T")
              setErrors((prev) => ({ ...prev, searchKeyword: "" }));
          }}
        />
        <InputField
          id="searchKeyword"
          name="searchKeyword"
          type={searchType === "T" ? "number" : "text"}
          inputClass="form-control"
          change={async (e) => {
            setSearchKeyword(e);
            if (searchType === "T") {
              debounceErrors(
                {
                  ...errors,
                  searchKeyword: await validate(e, "taxId"),
                },
                setErrors
              );
            } else debounceErrors({ ...errors, searchKeyword: "" }, setErrors);
          }}
          value={searchKeyword}
          wrapperClassName="col-md-3"
          error={errors.searchKeyword}
          touched={touched.searchKeyword}
          touch={() => setTouched({ ...touched, searchKeyword: true })}
          maxLength={searchType === "T" ? 9 : 20}
          ariaLabel="Search"
        />
        <InputField
          id="planId"
          maxLength={20}
          change={ownerInquiryPlanIdInputChange}
          value={ownerInquiryPlanId}
          wrapperClassName="col-md-3"
          placeholder={labels?.ownerInquiry?.planId}
        />
        <Button
          text={
            rowData
              ? labels?.ownerInquiry?.searchAgain
              : labels?.ownerInquiry?.search
          }
          wrapperClassName="col-md-2 mb-5"
          click={searchProductOwnerships}
          disabled={
            !searchKeyword?.trim() || Object.values(errors).some((err) => err)
          }
        />

        {rowData && (
          <>
            <Heading
              text={labels?.ownerInquiry?.ownerInquiryResults}
              wrapperClassNames="h4 border-bottom-1 pb-1 my-3"
              title={false}
            />
            <AgGrid
              rowData={rowData}
              columnDefs={columnDefs}
              wrapperClassNames="mb-3 d-none d-md-block"
              domLayout="normal"
              onRowClicked={onRowClickedOwnerInquiry}
              getRowClass={getRowClass}
              overlayNoRowsTemplate={overlayNoRowsTemplate}
            />
            <MobileResponsiveGrid
              key="ownerInquiryResults"
              rowData={rowData}
              columnDefs={columnDefs}
              getRowClass={getRowClass}
              onRowClick={onRowClickedOwnerInquiry}
              showSortDropDown={true}
              sortOption={{}}
              setSortOption={{}}
              dropdownData={[]}
            />

            {ownerData && (
              <>
                <OwnerDetails />
                <ProductDetails ownerData={ownerData} labels={labels} />
                <div className="d-md-flex justify-content-between mt-5 mb-3 w-100">
                  <Button
                    text={labels?.ownerInquiry?.accountDetails}
                    wrapperClassName="w-100 mb-2"
                    type={
                      selectedTab === "accountDetails" ? "primary" : "secondary"
                    }
                    click={() => handleTabChange("accountDetails")}
                  />

                  {showMoneySourceDetails && (
                    <Button
                      text={labels?.ownerInquiry?.moneySourceDetails}
                      wrapperClassName={"w-100 mb-2"}
                      type={
                        selectedTab === "moneySourceDetails"
                          ? "primary"
                          : "secondary"
                      }
                      click={() => handleTabChange("moneySourceDetails")}
                    />
                  )}

                  <Button
                    text={labels?.ownerInquiry?.transactionDetails}
                    wrapperClassName="w-100"
                    type={
                      selectedTab === "transactionDetails"
                        ? "primary"
                        : "secondary"
                    }
                    click={() => handleTabChange("transactionDetails")}
                  />
                </div>

                {selectedTab === "accountDetails" && (
                  <AccountDetails
                    accountDetails={ownerAccountDetails}
                    clickedOwnerData={ownerData}
                  />
                )}
                {selectedTab === "moneySourceDetails" && <MoneySourceDetails />}
                {selectedTab === "transactionDetails" && (
                  <TransactionDetails ownerData={ownerData} />
                )}
              </>
            )}
          </>
        )}
      </section>
    </div>
  );
};

export default OwnerInquiry;
